import React, { forwardRef } from "react";

import { BeautifulDialog, ShowErrorDialog } from "js/react/components/Dialogs/BaseDialog";
import { FlexLayout } from "js/Components/FlexLayout";
import { TextContent } from "js/Components/TextContent";
import { Button } from "js/Components/Button";
import FetchingClickShield from "js/react/components/FetchingClickShield";
import { getStaticUrl } from "js/config";
import AiGenerationService from "js/core/services/aiGeneration";
import { AiImageStyleTypes, ImageStyles } from "common/aiConstants";

import PromptInput from "../components/PromptInput";

function CustomImageStyleDialog(props: {
    theme?: any,
    imageStyle?: ImageStyles,
    imageStylePrompt?: string,
    hideThemeImageStyle?: boolean,
    onSuccess: (imageStyle: ImageStyles, imageStylePrompt: string) => void;
    closeDialog: () => void;
}, _ref: React.Ref<unknown>) {
    const { onSuccess, closeDialog } = props;

    const [imageStylePrompt, setImageStylePrompt] = React.useState<string>(props.imageStyle === "custom" ? props.imageStylePrompt : "");
    const [isGeneratingPreview, setIsGeneratingPreview] = React.useState<boolean>(false);
    const [previewImageUrl, setPreviewImageUrl] = React.useState<string | null>(null);

    const promptInputRef = React.useRef<HTMLInputElement>(null);

    const handlePromptChange = (prompt: string) => {
        setImageStylePrompt(prompt);
    };

    const handlePromptFinishTyping = (prompt: string) => {
        setImageStylePrompt(prompt);

        setTimeout(() => {
            if (promptInputRef.current) {
                promptInputRef.current.focus();
            }
        }, 100);
    };

    const handlePromptEnterKeyPress = (prompt: string) => {
        handleGeneratePreview(prompt);
    };

    const handleGeneratePreview = async (prompt: string) => {
        setIsGeneratingPreview(true);

        try {
            const { response: { assets } } = await AiGenerationService.generateImages({
                prompt: "plant",
                imageStyle: prompt,
                imagesCount: 1
            });

            setPreviewImageUrl(assets[0].readUrl);
            setIsGeneratingPreview(false);
        } catch (err) {
            ShowErrorDialog({
                title: "Oops, something went wrong, failed to generate preview",
                message: <>Please contact us at <a href="mailto:support@beautiful.ai">support@beautiful.ai</a> so we can help you directly.</>,
            });

            setIsGeneratingPreview(false);
        }
    };

    const handleDone = () => {
        onSuccess("custom", imageStylePrompt);
        closeDialog();
    };

    return (
        <BeautifulDialog
            PaperProps={{ style: { maxWidth: 850 } }}
            closeDialog={closeDialog}
        >
            <FlexLayout padding="40px 50px" gap={30} direction="column">
                <FlexLayout gap={10} direction="column">
                    <TextContent header>Define a custom style prompt</TextContent>
                    <TextContent subHeader>Create a custom style prompt for images generated with AI.</TextContent>
                </FlexLayout>

                <FlexLayout gap={20}>
                    <FlexLayout direction="column" width={"50%"} height="100%">
                        <PromptInput
                            inputRef={promptInputRef}
                            prompt={imageStylePrompt}
                            promptPlaceholder={"Describe the style of images used..."}
                            onPromptChange={handlePromptChange}
                            onPromptFinishTyping={handlePromptFinishTyping}
                            onEnterKeyPress={handlePromptEnterKeyPress}
                            previewButtonText="Generate Preview"
                            onPreviewButtonClick={() => handleGeneratePreview(imageStylePrompt)}
                            previewButtonDisabled={isGeneratingPreview}
                            inputContainerHeight={265}
                            imagePromptExamples={[!props.hideThemeImageStyle ? "theme" : null, ...Object.values(AiImageStyleTypes)].filter(Boolean) as ImageStyles[]}
                            hideSpeechToText={true}
                            multiline={true}
                            theme={props.theme}
                        />
                    </FlexLayout>
                    <FlexLayout direction="column" width={"50%"} height="100%" align="flex-end" justify="flex-start">
                        <FlexLayout justify="flex-end" border="1px solid #eee">
                            {isGeneratingPreview && <FlexLayout width={340} height={265}><FetchingClickShield backgroundColor={"white"} visible={true} /></FlexLayout>}
                            {!isGeneratingPreview && previewImageUrl && <img src={previewImageUrl} width={340} height={265} />}
                            {!isGeneratingPreview && !previewImageUrl && <img src={getStaticUrl("/images/ai-image-styles/stock.svg")} width={340} height={265} />}
                        </FlexLayout>
                    </FlexLayout>
                </FlexLayout>

                <FlexLayout gap={10} justify="flex-end">
                    <Button unframed onClick={closeDialog}>Cancel</Button>
                    <Button blue onClick={handleDone}>Save Custom Style</Button>
                </FlexLayout>
            </FlexLayout>
        </BeautifulDialog >
    );
}

export default forwardRef(CustomImageStyleDialog);
